import { fromJson } from '@client/core/data/concerns/parses-from-json';
import { defineTransform } from '@client/core/data/concerns/parses-from-json/transform-map';
import { Nullable } from '@client/core/data/types';
import { Project } from '@client/features/project/models/project';
import { ProjectFlipAnalysis } from '@client/features/flip-analyzer/models/project-flip-analysis';
import { ProjectRepairEstimate } from '@client/features/repair-estimator/models/project-repair-estimate';
import { ProjectStatus } from '@client/features/project/models/project-status';
import { ProjectRentalAnalysis } from '@client/features/rental-analyzer/models/project-rental-analysis';
import { ProjectBudget } from '@client/features/budget/models/project-budget';
import { ProjectMilestones } from '@client/features/project/models/project-milestones';
import { ProjectTasksSummary } from '@client/features/project-tasks/models/project-tasks-summary';
import { ProjectRisksSummary } from '@client/features/project-risks/models/project-risks-summary';
import { ProjectCard } from '@client/features/project/models/project-card';
import { ProjectStage } from '@client/features/project/types';
import { projectStageGroupText, projectStageText } from '@client/features/project/utils/project-stage';

/**
 * Project data displayed across various dashboards. Backend allows client to
 * customize which data is loaded.
 *
 * @see app/Http/Resources/Project/DashboardProjectResource.php
 */
export class DashboardProject {
  // Project Stage is usually read-only, but Dashboards allow changing this
  // within the UI. Delegate to the Project until User sets this within the UI.
  private editableStage: ProjectStage;

  constructor(
    public readonly project: Project,
    public readonly projectCard: ProjectCard,
    public readonly milestones: Nullable<ProjectMilestones>,
    public readonly flipAnalysis: Nullable<ProjectFlipAnalysis>,
    public readonly rentalAnalysis: Nullable<ProjectRentalAnalysis>,
    public readonly latestProjectStatus: Nullable<ProjectStatus>,
    public readonly repairEstimate: Nullable<ProjectRepairEstimate>,
    public readonly budget: Nullable<ProjectBudget>,
    public readonly tasksSummary: Nullable<ProjectTasksSummary>,
    public readonly risksSummary: Nullable<ProjectRisksSummary>,
  ) {}

  setEditableStage(stage: ProjectStage): void {
    this.editableStage = stage;
  }

  getEditableStage(): ProjectStage {
    return this.editableStage ? this.editableStage : this.project.stage;
  }

  /**
   * Text for the precise Project Stage this Project is in.
   */
  getEditableStageText(): string {
    const stage = this.getEditableStage();

    return projectStageText(stage);
  }

  /**
   * Text for the Group of Project Stages this Project is in.
   */
  getEditableStageGroupText(): string {
    const stage = this.getEditableStage();

    return projectStageGroupText(stage);
  }
}

defineTransform(DashboardProject, (json) => {
  return {
    project: fromJson(Project, json.project),
    projectCard: fromJson(ProjectCard, json.project_card),
    milestones: json.milestones ? fromJson(ProjectMilestones, json.milestones) : null,
    flipAnalysis: json.flip_analysis ? fromJson(ProjectFlipAnalysis, json.flip_analysis) : null,
    rentalAnalysis: json.rental_analysis ? fromJson(ProjectRentalAnalysis, json.rental_analysis) : null,
    latestProjectStatus: json.latest_project_status ? fromJson(ProjectStatus, json.latest_project_status) : null,
    repairEstimate: json.repair_estimate ? fromJson(ProjectRepairEstimate, json.repair_estimate) : null,
    budget: json.budget ? fromJson(ProjectBudget, json.budget) : null,
    tasksSummary: json.tasks_summary ? fromJson(ProjectTasksSummary, json.tasks_summary) : null,
    risksSummary: json.risks_summary ? fromJson(ProjectRisksSummary, json.risks_summary) : null,
  };
});
